/**
 * frontend/src/pages/Refunds.js
 * 
 * Public refund policy page.
 * Outlines the terms for subscriptions, free trials, cancellations, and refund requests.
 */
import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const Refunds = () => {
  return (
    <div className="landing-page">
      <SEO
        title="Refund Policy - ChangeFox"
        description="Review ChangeFox's refund policy, including information about trials, subscriptions, and cancellations."
        keywords="refund policy, cancellation, subscription, billing, changefox"
        path="/refunds"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Sign In</Link>
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Content */}
      <section className="legal-section">
        <Container>
          <motion.div
            className="legal-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="legal-title">Refund Policy</h1>
            <p className="legal-updated">Last updated: January 2026</p>

            <div className="legal-body">
              <h2>Overview</h2>
              <p>
                At ChangeFox, we want you to be completely satisfied with our service. This refund policy
                outlines the terms under which refunds may be issued for our compliance change management platform.
              </p>

              <h2>Free Trial</h2>
              <p>
                We offer a 14-day free trial for all new accounts. During this period, you can explore all
                features of ChangeFox without any payment. No credit card is required for the trial, and
                there's no obligation to continue after the trial ends.
              </p>

              <h2>Subscription Refunds</h2>
              <h3>Monthly Subscriptions</h3>
              <p>
                If you're not satisfied with your monthly subscription, you may request a refund within
                7 days of your initial payment. Refunds are not available for renewal payments on monthly plans.
              </p>

              <h3>Annual Subscriptions</h3>
              <p>
                For annual subscriptions, you may request a full refund within 30 days of your initial
                purchase. After 30 days, refunds will be prorated based on the unused portion of your
                subscription period.
              </p>

              <h2>How to Request a Refund</h2>
              <p>To request a refund, please:</p>
              <ol>
                <li>Email us at <a href="mailto:contact@changefox.ai">contact@changefox.ai</a></li>
                <li>Include your account email and the reason for your refund request</li>
                <li>Provide your order or invoice number if available</li>
              </ol>
              <p>
                We will review your request and respond within 3-5 business days. If approved, refunds
                will be processed to your original payment method within 5-10 business days.
              </p>

              <h2>Non-Refundable Items</h2>
              <p>The following are not eligible for refunds:</p>
              <ul>
                <li>Partial month usage after the refund period</li>
                <li>Add-on services or features purchased separately</li>
                <li>Accounts terminated due to violation of our Terms of Service</li>
                <li>Custom development or implementation services</li>
              </ul>

              <h2>Cancellation</h2>
              <p>
                You may cancel your subscription at any time from your account settings. Upon cancellation:
              </p>
              <ul>
                <li>Your account will remain active until the end of your current billing period</li>
                <li>You will not be charged for future billing periods</li>
                <li>Access to premium features will end when your subscription expires</li>
                <li>Your data will be retained for 30 days, after which it may be deleted</li>
              </ul>

              <h2>Service Credits</h2>
              <p>
                In cases where you experience significant service disruption or downtime beyond our SLA,
                we may offer service credits instead of refunds. Service credits will be applied to future
                billing periods.
              </p>

              <h2>Disputes</h2>
              <p>
                If you have a billing dispute, please contact us before initiating a chargeback with your
                payment provider. We're committed to resolving any issues fairly and promptly.
              </p>

              <h2>Changes to This Policy</h2>
              <p>
                We reserve the right to modify this refund policy at any time. Changes will be effective
                immediately upon posting. Your continued use of the service after changes constitutes
                acceptance of the new policy.
              </p>

              <h2>Contact Us</h2>
              <p>
                If you have questions about refunds or this policy, please contact us at{' '}
                <a href="mailto:contact@changefox.ai">contact@changefox.ai</a>.
              </p>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Refunds;
